import { API_BASE } from './constants'
import { FetchPolicyError, fetchWithPolicy } from '../../lib/api/fetchPolicy'
import type { Slide, SlideInput } from '../../types'
import {
  requestAuthed,
  resolveTokenFromAuthHeaders,
  toErrorMessage,
  type LegacyAuthHeaders,
} from '../../lib/api/http'

type Translator = (key: string, params?: Record<string, string | number>) => string

const OBJECT_ID_REGEX = /^[a-fA-F0-9]{24}$/

const withErrorCause = (message: string, cause: unknown): Error => {
  const symptomError = new Error(message) as Error & { cause?: unknown }
  symptomError.cause = cause
  return symptomError
}

const toObjectRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' ? (value as Record<string, unknown>) : {}

const toSlideId = (payload: Record<string, unknown>) => {
  const rawId =
    (typeof payload._id === 'string' && payload._id) ||
    (payload._id &&
    typeof (payload._id as { toString?: () => string }).toString === 'function'
      ? (payload._id as { toString: () => string }).toString()
      : undefined) ||
    (typeof payload.id === 'string' ? payload.id : undefined)

  if (!rawId || !OBJECT_ID_REGEX.test(rawId)) return null
  return rawId
}

const toSlide = (value: unknown): Slide | null => {
  const source = toObjectRecord(value)
  const id = toSlideId(source)
  if (!id) return null

  return {
    id,
    title: String(source.title ?? ''),
    description: String(source.description ?? ''),
    ctaLabel: typeof source.ctaLabel === 'string' ? source.ctaLabel : undefined,
    ctaHref: typeof source.ctaHref === 'string' ? source.ctaHref : undefined,
    image: typeof source.image === 'string' ? source.image : undefined,
  }
}

export const fetchSlidesSnapshot = async ({ t }: { t: Translator }): Promise<Slide[]> => {
  let response: Response
  try {
    response = await fetchWithPolicy({
      request: `${API_BASE}/settings/hero`,
      retries: 0,
    })
  } catch (error) {
    if (error instanceof FetchPolicyError) {
      throw withErrorCause(t('slider.toast.apiUnavailable'), error)
    }
    throw error
  }
  if (!response.ok) throw new Error(t('slider.toast.apiUnavailable'))

  const data = toObjectRecord(await response.json())
  const payload = Array.isArray(data.slides) ? data.slides : []
  return payload.map(toSlide).filter((slide): slide is Slide => Boolean(slide))
}

export const requestUpsertSlide = async ({
  authHeaders,
  editingId,
  payload,
  t,
}: {
  authHeaders: LegacyAuthHeaders
  editingId: string | null
  payload: SlideInput
  t: Translator
}) => {
  const token = resolveTokenFromAuthHeaders(authHeaders)
  const usePut = Boolean(editingId && OBJECT_ID_REGEX.test(editingId))
  const { response, payload: body } = await requestAuthed({
    token,
    path: usePut ? `/admin/settings/hero/${editingId}` : '/admin/settings/hero',
    init: {
      method: usePut ? 'PUT' : 'POST',
      headers: authHeaders(),
      body: JSON.stringify(payload),
    },
  })

  if (!response.ok) {
    throw new Error(
      `${t('slider.toast.saveErrorPrefix', { status: response.status })}: ${toErrorMessage(body, response.statusText)}`,
    )
  }
}

export const requestDeleteSlide = async ({
  authHeaders,
  id,
  t,
}: {
  authHeaders: LegacyAuthHeaders
  id: string
  t: Translator
}) => {
  const token = resolveTokenFromAuthHeaders(authHeaders)
  const { response } = await requestAuthed({
    token,
    path: `/admin/settings/hero/${id}`,
    init: {
      method: 'DELETE',
      headers: authHeaders(false),
    },
  })
  if (!response.ok) {
    throw new Error(t('slider.toast.deleteError'))
  }
}

export const requestReorderSlides = async ({
  authHeaders,
  orderIds,
  t,
}: {
  authHeaders: LegacyAuthHeaders
  orderIds: string[]
  t: Translator
}) => {
  if (orderIds.some((id) => !OBJECT_ID_REGEX.test(id))) {
    throw new Error(t('slider.toast.reorderInvalid'))
  }

  const token = resolveTokenFromAuthHeaders(authHeaders)
  const { response, payload } = await requestAuthed({
    token,
    path: '/admin/settings/hero/reorder',
    init: {
      method: 'PUT',
      headers: authHeaders(),
      body: JSON.stringify({ order: orderIds }),
    },
  })

  if (!response.ok) {
    throw new Error(
      `${t('slider.toast.reorderErrorPrefix', { status: response.status })}: ${toErrorMessage(payload, response.statusText)}`,
    )
  }
}
